import React, { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import PageHeader from '../../components/shell/PageHeader';
import ListScreen from '../../components/list/ListScreen';
import useListState from '../../components/list/useListState';
import UserCell from '../../components/ui/UserCell';
import StatusPill from '../../components/ui/StatusPill';
import Button from '../../components/ui/Button';
import Modal from '../../components/overlays/Modal';
import { Field, Textarea } from '../../components/ui/Field';
import { useOverlays } from '../../components/overlays/OverlayProvider';
import { getBankApprovalCounts, listBankApprovals } from '../../actions/agentAction';
import { updateAgentBankDetailStatus } from '../../actions/agentBankDetail';
import './partners.css';

const VIEWS = [
  { key: 'pending', label: 'Pending', attention: true },
  { key: 'approved', label: 'Approved' },
  { key: 'feedback', label: 'Feedback requested' },
  { key: 'all', label: 'All' },
];

/**
 * Bank approvals. Document review needs width, so the record opens as an inline
 * detail panel with the submitted fields on the left and document previews on the
 * right, rather than a 440px drawer.
 */
export default function BankApprovals() {
  const dispatch = useDispatch();
  const { confirm, toast } = useOverlays();
  const [record, setRecord] = useState(null);
  const [rejecting, setRejecting] = useState(false);
  const [reason, setReason] = useState('');

  const list = useListState({
    views: VIEWS,
    fetcher: listBankApprovals,
    countsFetcher: getBankApprovalCounts,
  });

  const refresh = useCallback(() => {
    setRecord(null);
    list.reload();
    list.reloadCounts();
  }, [list]);

  const approve = useCallback(
    async (row) => {
      const ok = await confirm({
        title: 'Approve bank details?',
        body: `${row.name}'s account and PAN will be marked verified, which allows payouts to be released to them.`,
        confirmLabel: 'Approve',
      });
      if (!ok) return;
      try {
        await updateAgentBankDetailStatus(dispatch, row.id, 'success');
        toast('Bank details approved', { tone: 'success' });
        refresh();
      } catch {
        toast('Could not approve bank details', { tone: 'error' });
      }
    },
    [confirm, dispatch, toast, refresh]
  );

  const submitRejection = useCallback(async () => {
    try {
      await updateAgentBankDetailStatus(dispatch, record.id, 'feedback_requested');
      toast('Feedback requested', { tone: 'success' });
      setRejecting(false);
      setReason('');
      refresh();
    } catch {
      toast('Could not request feedback', { tone: 'error' });
    }
  }, [dispatch, record, toast, refresh]);

  const columns = useMemo(
    () => [
      {
        key: 'partner',
        header: 'Partner',
        width: 240,
        sortable: true,
        render: (r) => <UserCell name={r.name || 'Unnamed'} secondary={r.email} seed={r.id} />,
      },
      { key: 'address', header: 'Address', muted: true, render: (r) => r.address || '—' },
      { key: 'ifsc_code', header: 'IFSC', width: 140, mono: true },
      { key: 'pan_number', header: 'PAN', width: 140, mono: true },
      {
        key: 'status',
        header: 'Status',
        width: 150,
        sortable: true,
        render: (r) => <StatusPill status={r.status} />,
      },
    ],
    []
  );

  const documents = record
    ? [
        { label: 'PAN card', doc: record.pan_card_image },
        { label: 'Bank proof', doc: record.bank_proof_image },
        { label: 'Address proof', doc: record.address_proof_image },
      ].filter((d) => d.doc?.url)
    : [];

  return (
    <>
      <PageHeader
        section="Partners"
        title={record ? record.name || 'Bank details' : 'Bank approvals'}
        subtitle={
          record
            ? 'Check the submitted documents against the account details.'
            : 'Verify account and PAN details before a partner can be paid.'
        }
        // The detail view is screen state rather than its own route, so the back
        // affordance is rendered in the body where it can reset that state.
      />

      {record ? (
        <div className="sv-screen-body">
          <button type="button" className="sv-page-header__back" onClick={() => setRecord(null)}>
            <i className="bx bx-arrow-back" aria-hidden="true" /> Back to bank approvals
          </button>

          <div className="sv-bank-detail">
            <section className="sv-card sv-bank-detail__fields">
              <h2 className="sv-card__title" style={{ padding: '18px 18px 0' }}>
                Submitted details
              </h2>
              <div style={{ padding: '8px 18px 18px' }}>
                {[
                  ['Account holder', record.name],
                  ['Account number', record.account_number],
                  ['IFSC code', record.ifsc_code],
                  ['PAN number', record.pan_number],
                  ['Address', record.address],
                  ['Email', record.email],
                ].map(([label, value]) => (
                  <div className="sv-drawer-field" key={label}>
                    <span className="sv-drawer-field__label">{label}</span>
                    <span className="sv-drawer-field__value sv-mono">{value || '—'}</span>
                  </div>
                ))}
                <div className="sv-drawer-field">
                  <span className="sv-drawer-field__label">Status</span>
                  <span className="sv-drawer-field__value">
                    <StatusPill status={record.status} />
                  </span>
                </div>
              </div>
            </section>

            <section className="sv-bank-detail__docs">
              {documents.length === 0 && (
                <div className="sv-card" style={{ padding: 24, fontSize: 13, color: 'var(--text-muted)' }}>
                  No documents were submitted.
                </div>
              )}
              {documents.map(({ label, doc }) => (
                <figure className="sv-doc-frame" key={label}>
                  <img src={doc.url} alt={label} />
                  <figcaption>{label}</figcaption>
                </figure>
              ))}
            </section>
          </div>

          <div className="sv-bank-detail__actions">
            <Button variant="primary" onClick={() => approve(record)}>
              Approve
            </Button>
            <Button variant="secondary" onClick={() => setRejecting(true)}>
              Request changes
            </Button>
          </div>
        </div>
      ) : (
        <ListScreen
          list={list}
          columns={columns}
          rowKey={(r) => r.id}
          noun="submissions"
          selectable={false}
          searchPlaceholder="Name or PAN"
          onRowClick={setRecord}
          empty={{
            icon: 'bx-buildings',
            title: 'No bank submissions',
            description: 'Nothing is waiting for verification.',
          }}
        />
      )}

      <Modal
        open={rejecting}
        onClose={() => setRejecting(false)}
        title="Request changes"
        size="form"
        confirmLabel="Send request"
        confirmDisabled={!reason.trim()}
        destructive
        onConfirm={submitRejection}
      >
        <p style={{ marginTop: 0 }}>
          {record?.name} will be asked to resubmit their documents. Tell them what needs fixing.
        </p>
        <Field label="Reason" required>
          <Textarea rows={4} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </Modal>
    </>
  );
}
